import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Empty, Spinner, fmtBRL } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Check, Truck, Wallet, Filter } from "lucide-react";

const empty = {
  motorista_id: "",
  motorista_nome: "",
  placa: "",
  valor: "",
  data: new Date().toISOString().slice(0, 10),
  descricao: "",
  shipment_id: "",
  status: "pendente",
  data_pagamento: "",
  metodo: "pix",
  observacoes: "",
};

export default function DriverFreights() {
  const [items, setItems] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [motoristaFilter, setMotoristaFilter] = useState("");
  const [placaFilter, setPlacaFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (motoristaFilter) params.motorista_id = motoristaFilter;
      const [f, m] = await Promise.all([
        api.get("/driver-freights", { params }),
        api.get("/motoristas"),
      ]);
      setItems(f.data || []);
      setMotoristas(m.data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, motoristaFilter]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); setErr(""); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      motorista_id: f.motorista_id || "",
      motorista_nome: f.motorista_nome || "",
      placa: f.placa || "",
      valor: f.valor || "",
      data: f.data || new Date().toISOString().slice(0, 10),
      descricao: f.descricao || "",
      shipment_id: f.shipment_id || "",
      status: f.status || "pendente",
      data_pagamento: f.data_pagamento || "",
      metodo: f.metodo || "pix",
      observacoes: f.observacoes || "",
    });
    setOpen(true); setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.motorista_nome.trim()) { setErr("Informe o motorista."); return; }
    if (!Number(form.valor) || Number(form.valor) <= 0) { setErr("Valor deve ser maior que zero."); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...form, valor: Number(form.valor) };
      if (editing) await api.put(`/driver-freights/${editing.id}`, payload);
      else await api.post("/driver-freights", payload);
      setOpen(false); await load();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const marcarPago = async (id) => {
    try { await api.post(`/driver-freights/${id}/pagar`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };
  const desmarcarPago = async (id) => {
    try { await api.post(`/driver-freights/${id}/desmarcar`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Excluir este frete?")) return;
    try { await api.delete(`/driver-freights/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  // Quando escolhe motorista do dropdown, autopreenche placa (primeira placa dele)
  const onSelectMotorista = (id) => {
    const m = motoristas.find((mm) => mm.id === id);
    setForm((f) => ({
      ...f,
      motorista_id: id,
      motorista_nome: m?.nome || "",
      placa: m?.placas?.[0] || f.placa,
    }));
  };

  // Lista de placas disponíveis derivada dos itens (backend não tem filtro por placa)
  const placas = useMemo(() => {
    const set = new Set();
    for (const i of items) {
      const p = (i.placa || "").trim().toUpperCase();
      if (p) set.add(p);
    }
    return Array.from(set).sort();
  }, [items]);

  // Filtro client-side por placa
  const filteredItems = useMemo(() => {
    if (!placaFilter) return items;
    const alvo = placaFilter.trim().toUpperCase();
    return items.filter((i) => (i.placa || "").trim().toUpperCase() === alvo);
  }, [items, placaFilter]);

  const totals = useMemo(() => {
    const pendente = filteredItems.filter((i) => i.status === "pendente").reduce((a, i) => a + Number(i.valor || 0), 0);
    const pago = filteredItems.filter((i) => i.status === "pago").reduce((a, i) => a + Number(i.valor || 0), 0);
    return { pendente, pago, total: pendente + pago };
  }, [filteredItems]);

  // Agrupa por motorista para "resumo"
  const porMotorista = useMemo(() => {
    const map = {};
    for (const i of filteredItems) {
      const key = i.motorista_nome || "—";
      if (!map[key]) map[key] = { nome: key, pendente: 0, pago: 0, qtd_pendente: 0, qtd_pago: 0 };
      const v = Number(i.valor || 0);
      if (i.status === "pago") { map[key].pago += v; map[key].qtd_pago += 1; }
      else { map[key].pendente += v; map[key].qtd_pendente += 1; }
    }
    return Object.values(map).sort((a, b) => b.pendente - a.pendente);
  }, [filteredItems]);

  return (
    <Layout
      title="Fretes de Motoristas"
      subtitle="Controle de valores a pagar para motoristas autônomos"
      actions={<Button onClick={openNew} data-testid="new-freight-button"><Plus size={16} /> Novo Frete</Button>}
    >
      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-amber-700">A pagar (pendente)</div>
          <div className="font-mono text-2xl font-bold text-amber-900 mt-1" data-testid="total-pendente">{fmtBRL(totals.pendente)}</div>
          <div className="text-[10px] text-amber-700 mt-0.5">{filteredItems.filter((i) => i.status === "pendente").length} fretes</div>
        </Card>
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-emerald-700">Já pago</div>
          <div className="font-mono text-2xl font-bold text-emerald-900 mt-1" data-testid="total-pago">{fmtBRL(totals.pago)}</div>
          <div className="text-[10px] text-emerald-700 mt-0.5">{filteredItems.filter((i) => i.status === "pago").length} fretes</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Total do período</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1" data-testid="total-geral">{fmtBRL(totals.total)}</div>
          <div className="text-[10px] text-ink-500 mt-0.5">{filteredItems.length} fretes cadastrados</div>
        </Card>
      </div>

      {/* Resumo por motorista */}
      {porMotorista.length > 0 && (
        <Card className="p-4 mb-5" data-testid="por-motorista-card">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={16} className="text-brand-600" />
            <h3 className="font-heading font-bold text-ink-900">A pagar por motorista</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {porMotorista.map((m) => (
              <div key={m.nome} className="flex items-center justify-between px-3 py-2 rounded bg-stone-50 border border-ink-200">
                <div className="flex items-center gap-2 min-w-0">
                  <Truck size={14} className="text-ink-400 shrink-0" />
                  <span className="truncate font-medium text-sm">{m.nome}</span>
                </div>
                <div className="text-right whitespace-nowrap">
                  {m.pendente > 0 && <div className="font-mono text-sm font-bold text-amber-800">{fmtBRL(m.pendente)}</div>}
                  {m.pago > 0 && <div className="font-mono text-[10px] text-emerald-700">Pago: {fmtBRL(m.pago)}</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex items-center gap-1 text-sm text-ink-500"><Filter size={14} /> Filtros:</div>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
          <option value="">Todos os status</option>
          <option value="pendente">Pendente</option>
          <option value="pago">Pago</option>
        </Select>
        <Select value={motoristaFilter} onChange={(e) => setMotoristaFilter(e.target.value)} data-testid="filter-motorista">
          <option value="">Todos os motoristas</option>
          {motoristas.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
        </Select>
        <Select value={placaFilter} onChange={(e) => setPlacaFilter(e.target.value)} data-testid="filter-placa">
          <option value="">Todas as placas</option>
          {placas.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
        {(statusFilter || motoristaFilter || placaFilter) && (
          <button onClick={() => { setStatusFilter(""); setMotoristaFilter(""); setPlacaFilter(""); }} className="text-xs text-brand-600 underline">Limpar filtros</button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : filteredItems.length === 0 ? (
        <Card><Empty title="Nenhum frete registrado" hint="Adicione um frete para começar." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2">Motorista</th>
                <th className="px-3 py-2">Placa</th>
                <th className="px-3 py-2">Descrição</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((f) => (
                <tr key={f.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors" data-testid={`freight-row-${f.id}`}>
                  <td className="px-3 py-2 font-mono text-ink-700 whitespace-nowrap">{f.data}</td>
                  <td className="px-3 py-2 font-medium text-ink-900">
                    {f.motorista_nome}
                    {f.auto_gerado && (
                      <span
                        className="ml-1.5 inline-block px-1 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-brand-100 text-brand-800 border border-brand-300"
                        title="Gerado automaticamente a partir da NF de entrada"
                        data-testid={`freight-auto-badge-${f.id}`}
                      >
                        auto
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-700">{f.placa || "—"}</td>
                  <td className="px-3 py-2 text-ink-700 max-w-xs truncate" title={f.descricao}>{f.descricao || "—"}</td>
                  <td className="px-3 py-2 font-mono text-right font-semibold text-ink-900">{fmtBRL(f.valor)}</td>
                  <td className="px-3 py-2">
                    {f.status === "pago" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                        <Check size={11} /> Pago
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                        Pendente
                      </span>
                    )}
                    {f.status === "pago" && f.data_pagamento && (
                      <div className="text-[9px] text-emerald-700 mt-0.5 font-mono">em {f.data_pagamento}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {f.status === "pendente" ? (
                      <button
                        onClick={() => marcarPago(f.id)}
                        className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-700"
                        title="Marcar como pago"
                        data-testid={`freight-pay-${f.id}`}
                      >
                        <Check size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => desmarcarPago(f.id)}
                        className="p-1.5 rounded-md hover:bg-amber-100 text-amber-700"
                        title="Desmarcar (voltar a pendente)"
                        data-testid={`freight-unpay-${f.id}`}
                      >
                        <X size={16} />
                      </button>
                    )}
                    <button onClick={() => openEdit(f)} className="p-1.5 rounded-md hover:bg-stone-100 text-ink-700" data-testid={`freight-edit-${f.id}`}><Pencil size={16} /></button>
                    <button onClick={() => onDelete(f.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" data-testid={`freight-delete-${f.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading font-bold text-ink-900">{editing ? "Editar Frete" : "Novo Frete"}</h3>
              <button onClick={() => setOpen(false)} className="text-ink-500 hover:text-ink-900"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Motorista</label>
                {motoristas.length > 0 ? (
                  <Select
                    value={form.motorista_id}
                    onChange={(e) => onSelectMotorista(e.target.value)}
                    data-testid="freight-motorista"
                  >
                    <option value="">— Selecionar ou digitar abaixo —</option>
                    {motoristas.map((m) => (
                      <option key={m.id} value={m.id}>{m.nome}{m.cpf ? ` — ${m.cpf}` : ""}</option>
                    ))}
                  </Select>
                ) : null}
                <input
                  type="text"
                  value={form.motorista_nome}
                  onChange={(e) => setForm({ ...form, motorista_nome: e.target.value, motorista_id: "" })}
                  placeholder="Nome do motorista (ou selecione acima)"
                  className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm mt-2"
                  data-testid="freight-motorista-nome"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Placa"
                  value={form.placa}
                  onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })}
                  data-testid="freight-placa"
                />
                <Input label="Data" type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="freight-data" />
              </div>
              <Input
                label="Valor (R$)"
                type="number" step="0.01" min="0" required
                value={form.valor}
                onChange={(e) => setForm({ ...form, valor: e.target.value })}
                data-testid="freight-valor"
              />
              <Input
                label="Descrição"
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Ex: Frete NF #1234 - São Paulo/SP"
                data-testid="freight-descricao"
              />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="freight-status">
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                </Select>
                <Select label="Método" value={form.metodo} onChange={(e) => setForm({ ...form, metodo: e.target.value })} data-testid="freight-metodo">
                  <option value="pix">PIX</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="transferencia">Transferência</option>
                  <option value="boleto">Boleto</option>
                  <option value="cheque">Cheque</option>
                  <option value="outro">Outro</option>
                </Select>
              </div>
              {form.status === "pago" && (
                <Input label="Data do pagamento" type="date" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} data-testid="freight-data-pag" />
              )}
              <Input label="Observações" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="freight-obs" />

              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="freight-submit">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
